import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme
} from '@mui/material';
import {
  DownhillSkiing as SkiIcon,
  Group as GroupIcon,
  Assessment as AssessmentIcon,
  Sports as SportsIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import Navbar from './Navbar';

function roundUpTo5(n) {
  return Math.ceil(n / 5) * 5;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  // Live stats state
  const [stats, setStats] = useState({ skis: 0, teams: 0, tests: 0 });

  useEffect(() => {
    fetch('/api/stats')
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(() => setStats({ skis: 100, teams: 50, tests: 1000 })); // fallback
  }, []);

  const features = [
    {
      icon: <SkiIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Ski Fleet Management',
      description: 'Track your entire ski collection with brand-specific specifications and detailed information.',
      color: 'primary'
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: theme.palette.secondary.main }} />,
      title: 'Performance Testing',
      description: 'Record and analyze ski performance tests to find your fastest skis for any condition.',
      color: 'secondary'
    },
    {
      icon: <SportsIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />,
      title: 'Race Tracking',
      description: 'Log race performances and track which skis work best in different conditions.',
      color: 'success'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40, color: theme.palette.info.main }} />,
      title: 'Team Collaboration',
      description: 'Work with coaches and teammates to share insights and optimize ski selection.',
      color: 'info'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: theme.palette.background.gradient }}>
      <Navbar />

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 12 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h1"
            sx={{
              mb: 3,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '4rem' }
            }}
          >
            Ski Fleet Management
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mb: 4,
              color: 'text.secondary',
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              fontWeight: 400
            }}
          >
            The modern way to manage your cross-country ski collection
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mb: 6 }}
          >
            {user ? (
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/dashboard')}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5b5eea 0%, #7c3aed 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)',
                  }
                }}
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5b5eea 0%, #7c3aed 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)',
                    }
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  Sign In
                </Button>
              </>
            )}
          </Stack>

          {/* Stats */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                {roundUpTo5(stats.skis)}+
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Skis Tracked
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: theme.palette.secondary.main, fontWeight: 700 }}>
                {roundUpTo5(stats.teams)}+
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Teams Using
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: theme.palette.success.main, fontWeight: 700 }}>
                {roundUpTo5(stats.tests)}+
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Tests Recorded
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: 8, maxWidth: 900, mx: 'auto' }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 6,
              fontWeight: 700
            }}
          >
            Everything you need to manage your ski fleet
          </Typography>

          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch"
          >
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card
                  sx={{
                    height: '100%',
                    width: '100%',
                    maxWidth: 400,
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                      borderColor: theme.palette[feature.color].main,
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: theme.palette[feature.color].main
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography
            variant="h3"
            sx={{
              mb: 3,
              fontWeight: 700
            }}
          >
            Ready to optimize your ski performance?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: 'text.secondary',
              fontWeight: 400
            }}
          >
            Join coaches and athletes who are already using Ski Fleet to improve their performance
          </Typography>
          {!user && (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5b5eea 0%, #7c3aed 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 35px rgba(99, 102, 241, 0.5)',
                }
              }}
            >
              Start Free Today
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
} 